/**
 * Application State Management - Reducer Pattern
 *
 * Single state object managed via reducer pattern per data-model.md
 */

/**
 * Initial application state
 */
export const initialState = {
  // FFmpeg initialization status
  ffmpegStatus: 'loading', // 'loading' | 'ready' | 'error'
  ffmpegError: null,

  // Input file
  file: null, // { name, size, duration, width, height, hasAudio, fileObject }
  fileError: null,

  // Configuration
  config: {
    repeatCount: 3,       // 2-10
    crossfadeDuration: 1.0 // 0.1-3.0 seconds
  },

  // Processing status
  processingStatus: 'idle', // 'idle' | 'processing' | 'complete' | 'error'
  processingProgress: 0,    // 0-100
  processingError: null,

  // Output
  output: null, // { blobUrl, fileSize, duration, filename }

  // UI state
  settingsChanged: false // True if config changed after processing complete
};

/**
 * Action types
 */
export const ActionTypes = {
  FFMPEG_LOADED: 'FFMPEG_LOADED',
  FFMPEG_ERROR: 'FFMPEG_ERROR',
  FILE_SELECTED: 'FILE_SELECTED',
  FILE_PROBED: 'FILE_PROBED',
  FILE_ERROR: 'FILE_ERROR',
  CONFIG_CHANGED: 'CONFIG_CHANGED',
  PROCESSING_START: 'PROCESSING_START',
  PROCESSING_PROGRESS: 'PROCESSING_PROGRESS',
  PROCESSING_COMPLETE: 'PROCESSING_COMPLETE',
  PROCESSING_ERROR: 'PROCESSING_ERROR',
  CLEAR_OUTPUT: 'CLEAR_OUTPUT'
};

/**
 * State reducer
 * @param {object} state - Current state
 * @param {object} action - Action with type and payload
 * @returns {object} New state
 */
export function reducer(state, action) {
  switch (action.type) {
    case ActionTypes.FFMPEG_LOADED:
      return {
        ...state,
        ffmpegStatus: 'ready',
        ffmpegError: null
      };

    case ActionTypes.FFMPEG_ERROR:
      return {
        ...state,
        ffmpegStatus: 'error',
        ffmpegError: action.payload.message
      };

    case ActionTypes.FILE_SELECTED:
      // Clear previous output when new file selected
      return {
        ...state,
        file: null,
        fileError: null,
        output: null,
        processingStatus: 'idle',
        processingProgress: 0,
        processingError: null,
        settingsChanged: false
      };

    case ActionTypes.FILE_PROBED:
      return {
        ...state,
        file: action.payload,
        fileError: null
      };

    case ActionTypes.FILE_ERROR:
      return {
        ...state,
        file: null,
        fileError: action.payload.message
      };

    case ActionTypes.CONFIG_CHANGED:
      return {
        ...state,
        config: {
          ...state.config,
          ...action.payload
        },
        // Mark settings as changed if output exists
        settingsChanged: state.output !== null
      };

    case ActionTypes.PROCESSING_START:
      return {
        ...state,
        processingStatus: 'processing',
        processingProgress: 0,
        processingError: null,
        settingsChanged: false
      };

    case ActionTypes.PROCESSING_PROGRESS:
      return {
        ...state,
        processingProgress: action.payload.progress
      };

    case ActionTypes.PROCESSING_COMPLETE:
      return {
        ...state,
        processingStatus: 'complete',
        processingProgress: 100,
        processingError: null,
        output: action.payload
      };

    case ActionTypes.PROCESSING_ERROR:
      return {
        ...state,
        processingStatus: 'error',
        processingError: action.payload.message
      };

    case ActionTypes.CLEAR_OUTPUT:
      return {
        ...state,
        output: null,
        processingStatus: 'idle',
        processingProgress: 0,
        settingsChanged: false
      };

    default:
      return state;
  }
}

/**
 * Create a store with the reducer pattern
 * @param {function} onChange - Callback when state changes
 * @returns {object} Store with getState, dispatch methods
 */
export function createStore(onChange = null) {
  let state = { ...initialState };
  const listeners = onChange ? [onChange] : [];

  function getState() {
    return state;
  }

  function dispatch(action) {
    const prevState = state;
    state = reducer(state, action);

    // Only notify if state actually changed
    if (state !== prevState) {
      listeners.forEach(listener => listener(state, prevState, action));
    }

    return state;
  }

  function subscribe(listener) {
    listeners.push(listener);
    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }

  return { getState, dispatch, subscribe };
}
